import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Hash, Inbox, MessageSquare, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Popover } from '../../../components/ui/popover';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../../components/ui/avatar';
import {
  AppBarBadgeButton,
  AppBarPopoverList,
  AppBarPopoverItem,
} from '../../../components/layout/appBarPopover';
import { useMessagingUnreadSummary } from '../hooks/useMessagingApi';
import { filterAndSortByPrioritizedSearch } from '../../../shared/utils/searchScore';
import { useRelativeDate } from '../../../shared/hooks';
import { CountBadge } from '../../../shared/components/common/CountBadge';
import type { UnreadConversationPreview } from '../../../generated/api/models/unreadConversationPreview';

/** Conversations previewed while the reader is just glancing at the badge. */
const RESTING_PREVIEW_LIMIT = 5;

/**
 * Conversations fetched while a filter is active — the unread-summary route's own
 * `limit` ceiling. Filtering the five-row preview would only ever hide rows the
 * reader can already see; widening it makes the field able to FIND something.
 */
const SEARCHING_PREVIEW_LIMIT = 20;

interface MessagingInboxPopoverProps {
  moduleColor: string;
  /**
   * Rendering variant (same fold pattern as AppBarMailFilesShortcuts /
   * AnnouncementsBell):
   * - `inline` (default): AppBar badge button + inbox preview popover (desktop).
   * - `menu`: a flat row for the mobile header overflow menu — navigates
   *   straight to `/messaging` (the vertical menu has no room for a nested
   *   popover), with the unread count as a trailing badge.
   */
  variant?: 'inline' | 'menu';
}

function ConversationAvatar({ row }: { row: UnreadConversationPreview }) {
  if (row.conversation_type === 'dm') {
    const initial = row.title?.charAt(0)?.toUpperCase() || '?';
    return (
      <Avatar className="h-7 w-7">
        {row.avatar_url && <AvatarImage src={row.avatar_url} alt={row.title} />}
        <AvatarFallback className="text-xs">{initial}</AvatarFallback>
      </Avatar>
    );
  }
  const Icon =
    row.conversation_type === 'public_channel' ||
    row.conversation_type === 'private_channel'
      ? Hash
      : Users;
  return (
    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
    </div>
  );
}

export function MessagingInboxPopover({ moduleColor, variant = 'inline' }: MessagingInboxPopoverProps) {
  const { t } = useTranslation(['messaging', 'common']);
  const navigate = useNavigate();
  const formatRelative = useRelativeDate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data, isLoading } = useMessagingUnreadSummary(
    query ? SEARCHING_PREVIEW_LIMIT : RESTING_PREVIEW_LIMIT,
  );
  const total = data?.total_unread ?? 0;
  const recent = useMemo(() => data?.recent_unread ?? [], [data]);

  // The conversation NAME is the subject; the last message (and who sent it) is the
  // body, so a title hit always outranks a preview one. Typo tolerance and
  // plural folding come from the shared search scorer, unchanged.
  const visible = useMemo(
    () =>
      filterAndSortByPrioritizedSearch(recent, query, {
        // Match the title as the reader SEES it, so an untitled conversation is
        // findable by the fallback label the row actually shows.
        primary: (row) => row.title || t('messaging:inboxPopover.untitled'),
        secondary: (row) =>
          `${row.last_message_sender_name ?? ''} ${row.last_message_preview ?? ''}`,
      }),
    [recent, query, t],
  );
  const isFiltering = query.length > 0;

  /**
   * Close the popover and drop the filter that goes with it.
   *
   * Both exits come through here, because Radix's `onOpenChange` fires only for
   * dismissals IT owns — an in-app `navigate()` is not one, so a row click that
   * only called `setOpen(false)` would leave the query behind, and the next open
   * would hide the reader's newest unread conversations behind a filter they had
   * forgotten typing.
   */
  const closePopover = () => {
    setOpen(false);
    setQuery('');
  };

  const handleSelect = (conversationId: string) => {
    closePopover();
    navigate(`/messaging?conversation=${conversationId}`);
  };

  const handleOpenAll = () => {
    closePopover();
    navigate('/messaging');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      closePopover();
      return;
    }
    setOpen(true);
  };

  // Mobile overflow: flat menu row → messaging inbox, unread as a trailing badge.
  if (variant === 'menu') {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 font-normal text-foreground"
        onClick={handleOpenAll}
      >
        <MessageSquare className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-start">{t('messaging:inboxPopover.title')}</span>
        <CountBadge count={total} ariaLabel={t('common:notifications.unreadCount', { count: total })} />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <AppBarBadgeButton
        icon={<MessageSquare className="h-4 w-4" />}
        count={total}
        ariaLabel={t('common:notifications.messagingAriaLabel')}
        tooltip={
          total > 0
            ? t('common:thread.unreadMessages', { count: total })
            : t('common:notifications.messagingTooltip')
        }
        moduleColor={moduleColor}
      />
      <AppBarPopoverList
        title={t('messaging:inboxPopover.title')}
        headerAction={
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={handleOpenAll}
          >
            {t('messaging:inboxPopover.openAll')}
          </Button>
        }
        search={{
          value: query,
          onChange: setQuery,
          placeholder: t('messaging:inboxPopover.searchPlaceholder'),
        }}
        isLoading={isLoading}
        isEmpty={visible.length === 0}
        emptyIcon={<Inbox className="h-8 w-8" />}
        emptyMessage={
          isFiltering
            ? t('messaging:inboxPopover.noSearchResults')
            : t('messaging:inboxPopover.empty')
        }
      >
        {visible.map((row) => {
          const senderPrefix = row.last_message_sender_name
            ? `${row.last_message_sender_name}: `
            : '';
          return (
            <AppBarPopoverItem
              key={row.conversation_id}
              onClick={() => handleSelect(row.conversation_id)}
              unread={row.unread_count > 0}
              leading={<ConversationAvatar row={row} />}
              title={row.title || t('messaging:inboxPopover.untitled')}
              subtitle={`${senderPrefix}${row.last_message_preview}`}
              meta={formatRelative(new Date(row.last_message_at))}
              trailing={
                <CountBadge
                  count={row.unread_count}
                  ariaLabel={t('common:notifications.unreadCount', { count: row.unread_count })}
                  className="shrink-0"
                />
              }
            />
          );
        })}
      </AppBarPopoverList>
    </Popover>
  );
}
